import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { AnimatePresence, motion } from 'framer-motion';
import { Star, X } from 'lucide-react';
import { Z_INDICES } from '../lib/zIndices';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const font = "'Inria Serif', serif";

function timeAgo(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

function StarRow({ value, size = 14 }) {
  const filled = Math.round(value);
  return (
    <div style={{ display: 'inline-flex', gap: 2 }}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} size={size} fill={n <= filled ? '#F4C54E' : 'transparent'} color={n <= filled ? '#F4C54E' : '#D4CFE4'} strokeWidth={2} />
      ))}
    </div>
  );
}

function Avatar({ name, photoUrl }) {
  const initial = (name || '?').trim().charAt(0).toUpperCase();
  if (photoUrl) {
    return <img src={photoUrl} alt={name || 'Guest'} style={{ width: 38, height: 38, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />;
  }
  return (
    <div style={{ width: 38, height: 38, borderRadius: '50%', flexShrink: 0, background: 'var(--accent-surface)', color: 'var(--accent-purple)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontFamily: font, fontSize: 16, fontWeight: 700 }}>
      {initial}
    </div>
  );
}

function Distribution({ reviews }) {
  const counts = useMemo(() => {
    const c = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    reviews.forEach((r) => {
      const n = Math.min(5, Math.max(1, Math.round(r.rating || 0)));
      c[n] += 1;
    });
    return c;
  }, [reviews]);
  const total = reviews.length || 1;

  return (
    <div data-testid="reviews-distribution" style={{ display: 'grid', gap: 6, padding: '6px 0' }}>
      {[5, 4, 3, 2, 1].map((n) => {
        const pct = Math.round((counts[n] / total) * 100);
        return (
          <div key={n} style={{ display: 'grid', gridTemplateColumns: '20px 1fr 30px', alignItems: 'center', gap: 10 }}>
            <span style={{ fontFamily: font, fontSize: 12.5, fontWeight: 700, color: 'var(--accent-text-soft)' }}>{n}★</span>
            <div style={{ height: 6, borderRadius: 999, background: 'var(--accent-surface-soft)', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${pct}%`, background: 'linear-gradient(90deg, #F4C54E, #F39B19)', borderRadius: 999, transition: 'width 240ms ease' }} />
            </div>
            <span style={{ fontFamily: font, fontSize: 12, color: 'var(--text-3)', fontWeight: 600, textAlign: 'right' }}>{counts[n]}</span>
          </div>
        );
      })}
    </div>
  );
}

function ReviewCard({ review }) {
  return (
    <div data-testid={`venue-review-${review.id}`} style={{ padding: '12px 0', borderTop: '1px solid var(--hairline)' }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <Avatar name={review.guest_name} photoUrl={review.guest_photo_url} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <p style={{ margin: 0, fontFamily: font, fontSize: 14.5, fontWeight: 700, color: 'var(--text)' }}>{review.guest_name || 'Allure guest'}</p>
            <span style={{ fontFamily: font, fontSize: 11.5, color: 'var(--text-3)', fontWeight: 600, flexShrink: 0 }}>{timeAgo(review.created_at)}</span>
          </div>
          <div style={{ marginTop: 4 }}>
            <StarRow value={review.rating} size={13} />
          </div>
          {review.review_text ? (
            <p style={{ margin: '8px 0 0', fontFamily: font, fontSize: 14, color: 'var(--text-2)', lineHeight: 1.5 }}>{review.review_text}</p>
          ) : null}
          {Array.isArray(review.photos) && review.photos.length ? (
            <div style={{ display: 'flex', gap: 8, marginTop: 10, overflowX: 'auto' }}>
              {review.photos.map((src) => (
                <img key={src} src={src} alt="Review" style={{ width: 92, height: 92, borderRadius: 14, objectFit: 'cover', flexShrink: 0 }} />
              ))}
            </div>
          ) : null}
          {review.venue_reply?.text ? (
            <div style={{ marginTop: 10, padding: '10px 12px', borderRadius: 14, background: 'var(--accent-surface-soft)' }}>
              <p style={{ margin: 0, fontFamily: font, fontSize: 12, fontWeight: 700, color: 'var(--accent-text-soft)', letterSpacing: '0.02em', textTransform: 'uppercase' }}>Venue reply</p>
              <p style={{ margin: '4px 0 0', fontFamily: font, fontSize: 13.5, color: 'var(--text-2)', lineHeight: 1.45 }}>{review.venue_reply.text}</p>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export function VenueReviewsModal({ open, onClose, venue }) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !venue?.id) return;
    setItems(null);
    setError(null);
    axios
      .get(`${API}/venue-reviews/by-venue/${venue.id}`)
      .then((res) => setItems(res.data?.items || []))
      .catch(() => setError('Could not load reviews. Please try again.'));
  }, [open, venue?.id]);

  const average = useMemo(() => {
    if (!items?.length) return Number(venue?.rating) || 0;
    const sum = items.reduce((a, r) => a + (Number(r.rating) || 0), 0);
    return sum / items.length;
  }, [items, venue?.rating]);

  return (
    <AnimatePresence>
      {open && venue ? (
        <>
          <motion.div
            key="reviews-backdrop"
            data-testid="venue-reviews-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            style={{ position: 'fixed', inset: 0, background: 'rgba(12, 10, 32, 0.58)', backdropFilter: 'blur(2px)', zIndex: Z_INDICES.modalOverlay }}
          />
          <motion.div
            key="reviews-modal"
            data-testid="venue-reviews-modal"
            initial={{ opacity: 0, y: 12, x: '-50%', scale: 0.97 }}
            animate={{ opacity: 1, y: 0, x: '-50%', scale: 1 }}
            exit={{ opacity: 0, y: 10, x: '-50%', scale: 0.97 }}
            transition={{ type: 'spring', stiffness: 340, damping: 30, mass: 0.7 }}
            style={{
              position: 'fixed',
              left: '50%',
              top: '50%',
              translateY: '-50%',
              width: 'calc(100% - 32px)',
              maxWidth: 440,
              maxHeight: 'calc(100vh - 80px)',
              background: 'var(--surface)',
              borderRadius: 26,
              overflow: 'hidden',
              boxShadow: '0 30px 60px rgba(20, 16, 50, 0.32)',
              display: 'flex',
              flexDirection: 'column',
              zIndex: Z_INDICES.modalOverlay + 1,
            }}
          >
            <div style={{ padding: '18px 20px 14px', borderBottom: '1px solid var(--hairline)' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                <div style={{ minWidth: 0 }}>
                  <p style={{ margin: 0, fontFamily: font, fontSize: 12.5, fontWeight: 600, color: 'var(--text-3)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Ratings & Reviews</p>
                  <p style={{ margin: '4px 0 0', fontFamily: font, fontSize: 19, fontWeight: 700, color: 'var(--text)', lineHeight: 1.15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{venue.name}</p>
                </div>
                <button
                  type="button"
                  data-testid="venue-reviews-modal-close"
                  onClick={onClose}
                  aria-label="Close reviews"
                  style={{ width: 34, height: 34, borderRadius: '50%', border: 'none', background: 'var(--accent-surface-soft)', color: 'var(--accent-text-soft)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                >
                  <X size={16} />
                </button>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 14 }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <p style={{ margin: 0, fontFamily: font, fontSize: 36, fontWeight: 700, color: 'var(--text)', lineHeight: 1 }}>{average ? average.toFixed(1) : '—'}</p>
                  <div style={{ marginTop: 6 }}><StarRow value={average} size={15} /></div>
                  <p data-testid="venue-reviews-count" style={{ margin: '6px 0 0', fontFamily: font, fontSize: 12.5, color: 'var(--text-3)', fontWeight: 600 }}>
                    {items === null ? 'Loading…' : `${items.length} ${items.length === 1 ? 'review' : 'reviews'}`}
                  </p>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  {items?.length ? <Distribution reviews={items} /> : null}
                </div>
              </div>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '4px 20px 20px' }}>
              {items === null ? (
                <div style={{ padding: '32px 0', textAlign: 'center', fontFamily: font, color: 'var(--text-3)', fontSize: 14 }}>
                  Loading reviews…
                </div>
              ) : error ? (
                <div style={{ padding: '32px 0', textAlign: 'center', fontFamily: font, color: 'var(--status-danger-text)', fontSize: 14 }}>
                  {error}
                </div>
              ) : items.length === 0 ? (
                <div style={{ padding: '36px 12px', textAlign: 'center', fontFamily: font, color: 'var(--text-3)' }}>
                  <Star size={28} color="var(--card-border)" style={{ margin: '0 auto 8px' }} />
                  <p style={{ fontSize: 15, fontWeight: 700, color: 'var(--accent-text-soft)', margin: '4px 0' }}>No reviews yet</p>
                  <p style={{ fontSize: 13, margin: 0 }}>Be one of the first to share your experience.</p>
                </div>
              ) : (
                <div>
                  {items.map((r) => (
                    <ReviewCard key={r.id} review={r} />
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        </>
      ) : null}
    </AnimatePresence>
  );
}

export default VenueReviewsModal;
